/**
 * Firestore Cache Service using IndexedDB
 * Provides TTL-based caching for all Firestore requests
 */

interface CacheEntry<T = any> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

interface CacheOptions {
  ttl?: number; // Default TTL in milliseconds (default: 5 minutes)
  forceRefresh?: boolean; // Skip cache and fetch fresh data
}

const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
const DB_NAME = 'NibJobsFirestoreCache';
const DB_VERSION = 1;
const STORE_NAME = 'queryCache';

class FirestoreCacheService {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  /**
   * Initialize IndexedDB
   */
  async init(): Promise<void> {
    if (this.db) return;
    
    if (this.initPromise) {
      return this.initPromise;
    }

    // Check if IndexedDB is available
    if (typeof indexedDB === 'undefined') {
      console.error('❌ IndexedDB is not available in this browser');
      return;
    }

    console.log('🔧 Initializing IndexedDB cache...');
    this.initPromise = new Promise<void>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('IndexedDB initialized successfully');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
          // Create an index for timestamp to help with cleanup
          objectStore.createIndex('timestamp', 'timestamp', { unique: false });
          console.log('Created object store:', STORE_NAME);
        }
      };
    });

    return this.initPromise;
  }

  /**
   * Generate a cache key from query parameters
   */
  private generateCacheKey(collectionPath: string, queryParams?: any): string {
    const params = queryParams ? JSON.stringify(queryParams) : '';
    return `${collectionPath}:${params}`;
  }

  /**
   * Get cached data if not expired
   */
  async get<T>(cacheKey: string): Promise<T | null> {
    try {
      await this.init();
      if (!this.db) return null;

      return new Promise<T | null>((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readonly');
        const objectStore = transaction.objectStore(STORE_NAME);
        const request = objectStore.get(cacheKey);

        request.onsuccess = () => {
          const entry = request.result as CacheEntry<T> | undefined;
          
          if (!entry) {
            resolve(null);
            return;
          }

          // Check if cache is expired
          const now = Date.now();
          const isExpired = now - entry.timestamp > entry.ttl;

          if (isExpired) {
            // Clean up expired entry
            this.delete(cacheKey).catch(console.error);
            console.log(`⏰ Cache expired for key: ${cacheKey.substring(0, 50)}...`);
            resolve(null);
          } else {
            const timeLeft = Math.round((entry.ttl - (now - entry.timestamp)) / 1000);
            console.log(`✅ Cache HIT for key: ${cacheKey.substring(0, 50)}... (${timeLeft}s remaining)`);
            resolve(entry.data);
          }
        };

        request.onerror = () => {
          console.error('Failed to get cached data:', request.error);
          resolve(null); // Fail gracefully
        };
      });
    } catch (error) {
      console.error('Cache get error:', error);
      return null; // Fail gracefully
    }
  }

  /**
   * Set cached data with TTL
   */
  async set<T>(cacheKey: string, data: T, ttl: number = DEFAULT_TTL): Promise<void> {
    try {
      await this.init();
      if (!this.db) return;

      const entry: CacheEntry<T> & { key: string } = {
        key: cacheKey,
        data,
        timestamp: Date.now(),
        ttl,
      };

      return new Promise<void>((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
        const objectStore = transaction.objectStore(STORE_NAME);
        const request = objectStore.put(entry);

        request.onsuccess = () => {
          console.log(`💾 Cached data for key: ${cacheKey.substring(0, 50)}... (TTL: ${Math.round(ttl/1000)}s)`);
          resolve();
        };

        request.onerror = () => {
          console.error('Failed to cache data:', request.error);
          resolve(); // Fail gracefully
        };
      });
    } catch (error) {
      console.error('Cache set error:', error);
      // Fail gracefully - don't block the app if caching fails
    }
  }

  /**
   * Delete a cached entry
   */
  async delete(cacheKey: string): Promise<void> {
    try {
      await this.init();
      if (!this.db) return;

      return new Promise<void>((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
        const objectStore = transaction.objectStore(STORE_NAME);
        const request = objectStore.delete(cacheKey);

        request.onsuccess = () => resolve();
        request.onerror = () => {
          console.error('Failed to delete cache entry:', request.error);
          resolve(); // Fail gracefully
        };
      });
    } catch (error) {
      console.error('Cache delete error:', error);
    }
  }

  /**
   * Clear all cached data
   */
  async clear(): Promise<void> {
    try {
      await this.init();
      if (!this.db) return;

      return new Promise<void>((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
        const objectStore = transaction.objectStore(STORE_NAME);
        const request = objectStore.clear();

        request.onsuccess = () => {
          console.log('Cache cleared successfully');
          resolve();
        };

        request.onerror = () => {
          console.error('Failed to clear cache:', request.error);
          resolve(); // Fail gracefully
        };
      });
    } catch (error) {
      console.error('Cache clear error:', error);
    }
  }

  /**
   * Clean up expired entries
   */
  async cleanupExpired(): Promise<void> {
    try {
      await this.init();
      if (!this.db) return;

      return new Promise<void>((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
        const objectStore = transaction.objectStore(STORE_NAME);
        const request = objectStore.openCursor();
        const now = Date.now();
        let deletedCount = 0;

        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result;
          
          if (cursor) {
            const entry = cursor.value as CacheEntry & { key: string };
            const isExpired = now - entry.timestamp > entry.ttl;
            
            if (isExpired) {
              cursor.delete();
              deletedCount++;
            }
            
            cursor.continue();
          } else {
            if (deletedCount > 0) {
              console.log(`Cleaned up ${deletedCount} expired cache entries`);
            }
            resolve();
          }
        };

        request.onerror = () => {
          console.error('Failed to cleanup expired entries:', request.error);
          resolve(); // Fail gracefully
        };
      });
    } catch (error) {
      console.error('Cache cleanup error:', error);
    }
  }

  /**
   * Get cache statistics
   */
  async getStats(): Promise<{ totalEntries: number; totalSize: number }> {
    try {
      await this.init();
      if (!this.db) return { totalEntries: 0, totalSize: 0 };

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readonly');
        const objectStore = transaction.objectStore(STORE_NAME);
        const request = objectStore.count();

        request.onsuccess = () => {
          resolve({
            totalEntries: request.result,
            totalSize: 0, // IndexedDB doesn't provide size info easily
          });
        };

        request.onerror = () => {
          console.error('Failed to get cache stats:', request.error);
          resolve({ totalEntries: 0, totalSize: 0 });
        };
      });
    } catch (error) {
      console.error('Cache stats error:', error);
      return { totalEntries: 0, totalSize: 0 };
    }
  }
}

// Export singleton instance
export const firestoreCache = new FirestoreCacheService();

// Export types and constants
export type { CacheOptions };
export { DEFAULT_TTL };
